import forge from 'apis/forge';
import Conversation from 'components/Conversation';
import Icon, { IconType } from 'components/Icon';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus, Quality } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveChat, selectActiveScene, selectChats } from 'redux/db/reducers';
import { getRightmostFrameX } from 'redux/db/util';
import { setActiveChatId, setGoldTooltipState, setSilverTooltipState, showContextMenu } from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import * as S from './styles';

const Chats = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [tooltipId] = useState(String(Math.random()));

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const { chats, activeChatId, activeScene, activeChatTitle, goldTooltipState, silverTooltipState, chatQuality } =
		useSelector((r: RootReducer) => {
			return {
				chats: selectChats(r.db),
				activeChatId: r.mode.activeChatId,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				activeChatTitle: selectActiveChat(r.db, r.mode.activeChatId)?.title || 'Untitled Chat',
				goldTooltipState: r.mode.goldTooltipState,
				silverTooltipState: r.mode.silverTooltipState,
				chatQuality: r.mode.chatQuality,
			};
		}, shallowEqual);
	const sortedChats = useMemo(() => {
		const sorted = [...chats].sort((a, b) => {
			return b.updatedAt.getTime() - a.updatedAt.getTime();
		});
		return sorted;
	}, [chats]);

	const historyMenuItems = useMemo(() => {
		if (sortedChats.length === 0) {
			return [new MenuItemAction('No chats available', '', () => {}, true)];
		}

		return sortedChats.map((chat) => {
			const isActive = chat.id === activeChatId;
			return new MenuItemAction(
				chat.title || 'Untitled Chat',
				'',
				() => dispatch(setActiveChatId(chat.id)),
				false,
				isActive // showCheckmark
			);
		});
	}, [sortedChats, activeChatId]);

	const moreMenuItems = useMemo(() => {
		if (!activeChatId) return [];
		return [
			MenuItemAction.createRenameChat(activeChatId),
			MenuItemAction.createSelectFramesFromChat(activeChatId),
		];
	}, [activeChatId]);

	useEffect(() => {
		if (!activeChatId || !sortedChats.some((chat) => chat.id === activeChatId)) {
			const firstChat = sortedChats.length > 0 ? sortedChats[0] : null;
			if (firstChat) {
				dispatch(setActiveChatId(firstChat.id));
			}
		}
	}, [sortedChats, activeChatId]);

	// activate the gold tooltip when:
	// - only one chat is open
	// - that chat has a single user message
	// - the current quality is silver
	useEffect(() => {
		if (
			goldTooltipState === 'uninitialized' &&
			chats.length === 1 &&
			chatQuality === Quality.SILVER &&
			activeChatId !== null &&
			activeChatId === chats[0].id
		) {
			const userEntryCount = chats[0].entries?.filter((entry) => entry.kind === 'user').length ?? 0;

			if (userEntryCount === 1) {
				dispatch(setGoldTooltipState('visible'));
			}
		}
	}, [dispatch, goldTooltipState, chatQuality, activeChatId, chats]);

	// activate the silver tooltip when:
	// - only one chat is open
	// - that chat has a single user message
	// - the current quality is gold
	useEffect(() => {
		if (
			silverTooltipState === 'uninitialized' &&
			chats.length === 1 &&
			chatQuality === Quality.GOLD &&
			activeChatId !== null &&
			activeChatId === chats[0].id
		) {
			const userEntryCount = chats[0].entries?.filter((entry) => entry.kind === 'user').length ?? 0;

			if (userEntryCount === 1) {
				dispatch(setSilverTooltipState('visible'));
			}
		}
	}, [dispatch, silverTooltipState, chatQuality, activeChatId, chats]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = async () => {
		if (!activeScene) return;
		if (activeScene.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		const chatId = uuid();
		const oldActiveChatId = activeChatId;
		const rightmostFrameX = getRightmostFrameX();
		dispatch(setActiveChatId(chatId));

		try {
			await forge.createChat(
				{
					id: chatId,
					sceneId: activeScene.id,
					title: NEW_CHAT_TITLE,
					x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
					y: 0,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
				},
				true
			);
		} catch (e) {
			dispatch(setActiveChatId(oldActiveChatId));
			throw e;
		}

		// we intentionally do NOT update the pending chat input here.  the ChatInput component
		// owns it and manages that process - ss
	};

	const onHeaderContextMenu = (e: React.MouseEvent) => {
		if (!activeChatId) return;
		if (e.target instanceof HTMLElement) {
			dispatch(
				showContextMenu({
					point: { x: e.clientX, y: e.clientY },
					menuItems: [MenuItemAction.createRenameChat(activeChatId), ...moreMenuItems],
				})
			);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderButton = (icon: IconType, onClick: () => void, tooltip: string) => {
		return (
			<>
				<S.Button onClick={onClick} data-tooltip-id={tooltipId} data-tooltip-content={tooltip}>
					<Icon type={icon} width={16} height={16} color={theme.gray['1000']} />
				</S.Button>
				<Tooltip id={tooltipId} placement="top" />
			</>
		);
	};

	return (
		<S.Base ref={ref}>
			<S.Header onContextMenu={onHeaderContextMenu}>
				<S.Label>{activeChatTitle}</S.Label>
				<S.ButtonGroup>
					{renderButton(IconType.CHAT_NEW, onNewChat, 'Create new chat')}
					<DropdownMenu menuItems={historyMenuItems}>
						<S.DropdownTrigger data-tooltip-id={tooltipId} data-tooltip-content="Chat history">
							<Icon type={IconType.HISTORY} width={16} height={16} color={theme.gray['1000']} />
						</S.DropdownTrigger>
					</DropdownMenu>
					<DropdownMenu menuItems={moreMenuItems}>
						<S.DropdownTrigger>
							<Icon type={IconType.MORE} width={16} height={16} color={theme.gray['1000']} />
						</S.DropdownTrigger>
					</DropdownMenu>
					<Tooltip id={tooltipId} placement="top" />
				</S.ButtonGroup>
			</S.Header>
			{activeChatId && (
				<S.Conversation>
					<Conversation />
				</S.Conversation>
			)}
		</S.Base>
	);
};

export default Chats;
